// src/services/api.js
import axios from 'axios';

const BASE = 'https://rh.gfm3.org/api';

const api = axios.create({ baseURL: BASE });

// Injecter le token JWT dans chaque requête
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('gfm3_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Gérer les 401 → rediriger vers login
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('gfm3_token');
      localStorage.removeItem('gfm3_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const login  = (data) => api.post('/auth/login', data);
export const logout = ()     => api.post('/auth/logout');

// ── Dashboard ─────────────────────────────────────────────────
export const getDashboard = (params) => api.get('/dashboard', { params });

// ── Employés ──────────────────────────────────────────────────
export const getEmployes   = (params) => api.get('/employes', { params });
export const getEmploye    = (id)     => api.get(`/employes/${id}`);
export const createEmploye = (data)   => api.post('/employes', data);
export const updateEmploye = (id, d)  => api.put(`/employes/${id}`, d);
export const deleteEmploye    = (id)     => api.delete(`/employes/${id}`);
export const getNextMatricule = ()       => api.get('/employes/next-matricule');

// ── Historique des postes (§3.1.2) ────────────────────────────
export const getHistoriquePostes   = (id)          => api.get(`/employes/${id}/historique`);
export const createHistoriquePoste = (id, d)       => api.post(`/employes/${id}/historique`, d);
export const updateHistoriquePoste = (id, mvtId, d)=> api.put(`/employes/${id}/historique/${mvtId}`, d);
export const deleteHistoriquePoste = (id, mvtId)   => api.delete(`/employes/${id}/historique/${mvtId}`);

// ── Documents administratifs / GED (§3.1.3) ───────────────────
export const getDocumentsRH    = (id)           => api.get(`/employes/${id}/documents`);
export const createDocumentRH  = (id, d)        => api.post(`/employes/${id}/documents`, d);
export const updateDocumentRH  = (id, docId, d) => api.put(`/employes/${id}/documents/${docId}`, d);
export const deleteDocumentRH  = (id, docId)    => api.delete(`/employes/${id}/documents/${docId}`);
export const uploadDocumentRH  = (id, docId, formData) =>
  api.post(`/employes/${id}/documents/${docId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// ── Congés ────────────────────────────────────────────────────
export const getConges      = (params) => api.get('/conges', { params });
export const getConge       = (id)     => api.get(`/conges/${id}`);
export const createConge    = (data)   => api.post('/conges', data);
export const updateConge    = (id, d)  => api.put(`/conges/${id}`, d);
export const approuverConge = (id)     => api.put(`/conges/${id}/approuver`);
export const rejeterConge   = (id)     => api.put(`/conges/${id}/rejeter`);
export const deleteConge    = (id)     => api.delete(`/conges/${id}`);

// ── Feuilles de temps ─────────────────────────────────────────
export const getFeuilles   = (params) => api.get('/temps', { params });
export const getFeuille    = (id)     => api.get(`/temps/${id}`);
export const createFeuille = (data)   => api.post('/temps', data);
export const updateFeuille = (id, d)  => api.put(`/temps/${id}`, d);
export const deleteFeuille = (id)     => api.delete(`/temps/${id}`);

// ── Validation comptabilité — niveau 3 (§3.4) ─────────────────
export const validerFeuilleCom = (id, d = {}) => api.post(`/temps/${id}/valider-compta`, d);
export const rejeterFeuilleCom = (id, d = {}) => api.post(`/temps/${id}/rejeter-compta`, d);

// ── Bulletins de paie ─────────────────────────────────────────
export const getBulletins   = (params) => api.get('/paie/bulletins', { params });
export const getBulletin    = (id)     => api.get(`/paie/bulletins/${id}`);
export const createBulletin = (data)   => api.post('/paie/bulletins', data);
export const deleteBulletin = (id)     => api.delete(`/paie/bulletins/${id}`);
// §3.31/§3.36 — Générations en masse (bulletins/factures et feuilles de temps)
export const genererBulletinsMasse = (data) => api.post('/paie/bulletins/generer-masse', data, { timeout: 600000 });
export const genererFeuillesMasse  = (data) => api.post('/temps/generer-masse', data, { timeout: 600000 });

// ── §3.58 Solde de tout compte (droits de fin de contrat) ─────
export const getSdtcList    = (params) => api.get('/sdtc', { params });
export const getSdtc        = (id)     => api.get(`/sdtc/${id}`);
export const preparerSdtc   = (empId)  => api.get(`/sdtc/preparer/${empId}`);
export const simulerSdtc    = (data)   => api.post('/sdtc/simuler', data);
export const createSdtc     = (data)   => api.post('/sdtc', data);
export const updateSdtc     = (id, d)  => api.put(`/sdtc/${id}`, d);
export const deleteSdtc     = (id)     => api.delete(`/sdtc/${id}`);

// ── Factures consultants ──────────────────────────────────────
export const getFactures   = (params) => api.get('/paie/factures', { params });
export const getFacture    = (id)     => api.get(`/paie/factures/${id}`);
export const createFacture = (data)   => api.post('/paie/factures', data);
export const updateFacture = (id, d)  => api.put(`/paie/factures/${id}`, d);
export const deleteFacture = (id)     => api.delete(`/paie/factures/${id}`);

// ── Référentiels ──────────────────────────────────────────────
export const getDepartements = () => api.get('/referentiels/departements');
export const getFonctions    = () => api.get('/referentiels/fonctions');
export const getProjets      = () => api.get('/projets');              // → projets_bailleurs

// ── Bureaux (§Bureau) ─────────────────────────────────────────
export const getBureaux            = ()           => api.get('/bureaux');
export const getEmployesByBureau   = (id)         => api.get(`/bureaux/${id}/employes`);
export const affecterBureau        = (empId, bureauId) =>
  api.put(`/employes/${empId}/bureau`, { bureau_id: bureauId });

// ── Pointage ──────────────────────────────────────────────────
export const getPointageJour          = (params)     => api.get('/pointage', { params });
export const upsertPointage           = (data)       => api.post('/pointage', data);
export const batchPointage            = (data)       => api.post('/pointage/batch', data);
export const deletePointage           = (id)         => api.delete(`/pointage/${id}`);
export const getPointageStats         = (params)     => api.get('/pointage/stats', { params });
export const getPointageAgent         = (id, params) => api.get(`/pointage/agent/${id}`, { params });
export const getPointageParametres    = ()           => api.get('/pointage/parametres');
export const updatePointageParametres = (data)       => api.put('/pointage/parametres', data);

export default api;
